import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

type OrientLock = ScreenOrientation & {
  lock?: (orientation: string) => Promise<void>;
  unlock?: () => void;
};

function getScreenOrientation(): OrientLock | undefined {
  return screen.orientation as OrientLock | undefined;
}

/** Fullscreen chart overlay with best-effort landscape lock (PWA / Android). */
export function useChartLandscapeFullscreen() {
  const [open, setOpen] = useState(false);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const ownsBrowserFullscreenRef = useRef(false);
  const fullscreenRequestRef = useRef<Promise<void>>(Promise.resolve());

  const exit = useCallback(async () => {
    try {
      if (ownsBrowserFullscreenRef.current && document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch {
      /* unsupported or already exited */
    }
    ownsBrowserFullscreenRef.current = false;
    try {
      getScreenOrientation()?.unlock?.();
    } catch {
      /* iOS may not support unlock */
    }
    setOpen(false);
  }, []);

  const enter = useCallback(() => {
    /*
     * Fullscreen must be requested synchronously from the click handler.
     * Waiting for the portal to mount loses transient user activation on
     * Android Chrome, which also makes orientation.lock() fail.
     */
    try {
      if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
        fullscreenRequestRef.current = document.documentElement
          .requestFullscreen({ navigationUI: 'hide' })
          .then(() => {
            ownsBrowserFullscreenRef.current = true;
          })
          .catch(() => undefined);
      } else {
        fullscreenRequestRef.current = Promise.resolve();
      }
    } catch {
      fullscreenRequestRef.current = Promise.resolve();
    }
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    let cancelled = false;

    /** Wait until the portal overlay is mounted so requestFullscreen has a target. */
    const waitForOverlay = async (): Promise<HTMLElement | null> => {
      for (let i = 0; i < 12; i++) {
        if (overlayRef.current) return overlayRef.current;
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      }
      return overlayRef.current;
    };

    const enterLandscapeFullscreen = async () => {
      await fullscreenRequestRef.current;
      if (cancelled) return;

      const el = await waitForOverlay();
      if (cancelled || !el) return;

      const orientation = getScreenOrientation();
      if (orientation?.lock) {
        const modes = ['landscape-primary', 'landscape', 'landscape-secondary'] as const;
        for (const mode of modes) {
          try {
            await orientation.lock(mode);
            delete el.dataset.simulatedLandscape;
            return;
          } catch {
            /* try next mode */
          }
        }
      }

      // iOS and browsers without orientation lock still get a usable landscape canvas.
      if (window.innerHeight > window.innerWidth) {
        el.dataset.simulatedLandscape = 'true';
      }
    };

    void enterLandscapeFullscreen();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') void exit();
    };
    const onFullscreenChange = () => {
      if (!ownsBrowserFullscreenRef.current || document.fullscreenElement) return;
      ownsBrowserFullscreenRef.current = false;
      try {
        getScreenOrientation()?.unlock?.();
      } catch {
        /* unsupported */
      }
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onFullscreenChange);

    return () => {
      cancelled = true;
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, exit]);

  useEffect(() => {
    return () => {
      try {
        getScreenOrientation()?.unlock?.();
      } catch {
        /* ignore */
      }
      try {
        if (ownsBrowserFullscreenRef.current && document.fullscreenElement) {
          ownsBrowserFullscreenRef.current = false;
          void document.exitFullscreen();
        }
      } catch {
        /* ignore */
      }
    };
  }, []);

  return { open, enter, exit, overlayRef };
}

/** Plot height for landscape fullscreen — measures overlay body after rotate. */
export function useFullscreenChartHeight(
  active: boolean,
  overlayRef: RefObject<HTMLElement | null>,
  reservedPx = 108,
): number {
  const [height, setHeight] = useState(280);

  useEffect(() => {
    if (!active) return;

    const measure = () => {
      const body = overlayRef.current?.querySelector('.pdc-fullscreen-overlay__body');
      const bodyH = body instanceof HTMLElement ? body.clientHeight : 0;
      const w = window.innerWidth;
      const h = window.innerHeight;
      const landscape = w > h;

      if (bodyH > 0 && body instanceof HTMLElement) {
        const controls = body.querySelector('.pdc-chart__controls');
        const legend = body.querySelector('.pdc-chart__legend');
        const chrome =
          (controls instanceof HTMLElement ? controls.offsetHeight : 0)
          + (legend instanceof HTMLElement ? legend.offsetHeight : 0)
          + 12;
        setHeight(Math.max(180, bodyH - chrome));
        return;
      }

      const reserved = landscape ? reservedPx : reservedPx + 40;
      setHeight(Math.max(180, h - reserved));
    };

    measure();
    const timers = [50, 180, 400, 750].map((ms) => window.setTimeout(measure, ms));
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);

    const body = overlayRef.current?.querySelector('.pdc-fullscreen-overlay__body');
    let ro: ResizeObserver | undefined;
    if (body instanceof HTMLElement && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(body);
    }

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      ro?.disconnect();
    };
  }, [active, overlayRef, reservedPx]);

  return height;
}
